import { useState, useEffect } from 'react';
import api from 'services/api';
import { useAuth } from 'context/AuthContext';
import { useTenant } from 'context/TenantContext';
import ClientPicker, { clientPayload, clientValueValid, emptyClientValue } from 'components/shared/ClientPicker';
import MontantInput from 'components/shared/MontantInput';
import { formatMontant } from 'components/shared/montant';
import { definition, normaliser, versJsonb, valider, lignesAffichage,
         LIBELLE_UNITE, rendreValeur, estValeurValide, libelleAffiche,
         unitesAutorisees, valeurHorsPlage } from './composants-offre';

// GRILLE DES COMPOSANTS + libelles : deplacee dans ./composants-offre.js.
// Elle y est TYPEE (certains composants portent un parametre d'engagement) et
// testee. Les anciens SERVICE_ORDER / SERVICE_LABELS / DelaiLine / SlaLine ont
// ete retires : le libelle est desormais COMPOSE avec son parametre.
// ⚠️ AVANT : `CARD_STYLES[o.code]` — le style etait indexe sur le CODE de l'offre,
// et les 3 seules cles etaient ESS/PRO/PRM, soit les codes du SEED FOX. Tout autre
// tenant tombait sur le repli blanc : les 2 offres de GLADIATOR sortaient
// IDENTIQUES, et l'une d'elles n'avait un style que par coincidence de code.
// Meme famille de defaut que le catalogue fige sur les prix de Fox : un
// referentiel editeur bati autour d'UN tenant, qui ne degrade pas pour les autres.
//
// DESORMAIS : le style suit la colonne `ordre` (decision Dom, 25/08/2026).
// Elle existe deja, elle est deja editable, elle porte deja l'intention de
// progression commerciale — et elle NE BOUGE PAS quand les prix changent.
// (Le rang de prix a ete ecarte : la carte vedette migrerait a chaque operation
//  commerciale, ce qui serait un defaut, pas une fonctionnalite.)
const STYLE_NEUTRE       = { bg: '#FFFFFF', border: '#E0E0E0', title: '#333' };
const STYLE_INTERMEDIAIRE = { bg: '#F0F4FF', border: '#C8A84E', title: '#1B3A5C' };
const STYLE_VEDETTE      = { bg: '#1A1A2E', border: '#C8A84E', title: '#FFFFFF', dark: true };

/**
 * Style par POSITION dans la gamme, triee par `ordre`.
 *   premiere      -> neutre
 *   derniere      -> vedette (le haut de gamme se voit)
 *   intermediaire -> intermediaire
 * Une gamme d'UNE SEULE offre reste neutre : sans progression, il n'y a pas de
 * vedette a designer.
 */
function styleParRang(rang, total) {
  if (total <= 1) return STYLE_NEUTRE;
  if (rang === 0) return STYLE_NEUTRE;
  if (rang === total - 1) return STYLE_VEDETTE;
  return STYLE_INTERMEDIAIRE;
}

function ServiceLine({ label, included, dark }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, marginBottom: 4, fontWeight: 400,
      color: dark ? (included ? '#E0E0E0' : '#555') : (included ? '#333' : '#BBB') }}>
      <span style={{ color: included ? '#22C55E' : '#DC2626', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
        {included ? '\u2713' : '\u2717'}
      </span>
      {label}
    </div>
  );
}

export default function OffresPage() {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // Admin
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(r => ['admin', 'dg'].includes(r));

  // DEVISE DU TENANT — lue depuis public.tenants.devise (NOT NULL) via
  // /api/v1/tenant/branding. PLUS de devise en dur ici : un tenant camerounais est en
  // XAF, un ghaneen en GHS. Chaine vide tant que le branding n'est pas charge :
  // afficher un montant dans la MAUVAISE devise serait pire que sans unite.
  const tenant = useTenant();
  const DEVISE = tenant?.devise || '';
  const [adminOffres, setAdminOffres] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [adminMsg, setAdminMsg] = useState(null);

  // COMPOSITION D'OFFRE — brouillon de la modale.
  // `cible` = 'new' (ligne de creation) ou l'id d'une offre existante.
  // Le brouillon n'est ecrit qu'a la validation : fermer sans valider n'altere rien.
  const [compo, setCompo] = useState(null);
  const [compoMsg, setCompoMsg] = useState(null);

  // Tarifs des prestations du tenant (table tarifs_prestations). Deux usages :
  // l'ecran d'edition admin plus bas, ET la modale devis (source des montants
  // add-on, qui etaient en dur 25000/15000/50000 avant l'etape 3b).
  const [tarifsPrest, setTarifsPrest] = useState([]);
  const [loadingTarifs, setLoadingTarifs] = useState(true);
  const [editTarif, setEditTarif] = useState(null);
  const [tarifMsg, setTarifMsg] = useState(null);

  // Simulator
  const [simOffre, setSimOffre] = useState('');
  const [nbSites, setNbSites] = useState(1);
  const [agentsJour, setAgentsJour] = useState(1);
  const [agentsNuit, setAgentsNuit] = useState(1);

  // Modals
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [clientEmail, setClientEmail] = useState('');

  // Devis composition. Aucun montant en dur : comme le gardiennage (deja aligne
  // sur offres.tarif_agent_mois), les 3 add-on partent a 0 et sont injectes a
  // l'ouverture de la modale depuis /tarifs-prestations. La periodicite suit la
  // meme source ; les valeurs ci-dessous ne servent que tant que la modale n'a
  // pas ete ouverte.
  // `prixDefini` : la prestation a-t-elle un prix en base ? (montant NON NULL)
  // Une prestation sans prix n'est PAS selectionnable — cf. commentaire de
  // appliquerTarifs. Le gardiennage vient d'`offres`, il a toujours un tarif.
  const [devisSections, setDevisSections] = useState({
    gardiennage: { actif: true, agents: 1, tarif: 0, periodicite: 'mensuel', prixDefini: true },  // A
    video: { actif: false, cameras: 4, tarif: 0, periodicite: 'ponctuel', prixDefini: false },    // B
    alarmes: { actif: false, zones: 2, tarif: 0, periodicite: 'ponctuel', prixDefini: false },    // C
    tsv: { actif: false, forfait: 0, periodicite: 'mensuel', prixDefini: false },                 // D
  });
  const [clients, setClients] = useState([]);
  const [clientValue, setClientValue] = useState(emptyClientValue);
  const [savingDevis, setSavingDevis] = useState(false);
  const [devisObjet, setDevisObjet] = useState('');
  const [devisDateValidite, setDevisDateValidite] = useState('');

  const loadAdminOffres = () => {
    api.get('/offres/all').then(r => setAdminOffres(r.data)).catch(() => {});
  };

  const loadTarifsPrest = () => {
    setLoadingTarifs(true);
    api.get('/tarifs-prestations')
      .then(r => setTarifsPrest(r.data))
      .catch(() => setTarifsPrest([]))
      .finally(() => setLoadingTarifs(false));
  };

  useEffect(() => {
    api.get('/offres').then(r => {
      setOffres(r.data);
      if (r.data.length > 0) setSimOffre(r.data[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  // Tarifs prestations : charges pour TOUS les roles (la modale devis en depend,
  // et le responsable_commercial n'est pas admin). L'ecran d'edition, lui, reste
  // sous garde isAdmin a l'affichage.
  useEffect(() => { loadTarifsPrest(); }, []);

  useEffect(() => {
    if (isAdmin) loadAdminOffres();
  }, [isAdmin]);

  const simOffreObj = offres.find(o => o.id === simOffre);
  const totalAgents = nbSites * (agentsJour + agentsNuit);
  const tarif = Number(simOffreObj?.tarif_agent_mois || 0);
  const mensuel = totalAgents * tarif;
  const trimestriel = Math.round(mensuel * 3 * 0.95);
  const annuel = Math.round(mensuel * 12 * 0.85);

  // Codes du catalogue fige (cf. migration tarifs_prestations_tenant.sql) : le
  // catalogue est en code, seuls les PRIX viennent de la base.
  const LIBELLES_PRESTA = { video: 'Vidéosurveillance', alarmes: 'Alarmes & Détection', tsv: 'Télésurveillance' };
  // DEUX etats distincts, a ne pas confondre :
  //  - ligne ABSENTE du catalogue du tenant       -> tarifsManquants
  //  - ligne PRESENTE mais montant NULL en base   -> prixNonDefinis ("a renseigner")
  const tarifsManquants = Object.keys(LIBELLES_PRESTA).filter(c => !tarifsPrest.some(t => t.code === c));
  const prixNonDefinis = Object.keys(LIBELLES_PRESTA).filter(c => {
    const t = tarifsPrest.find(x => x.code === c);
    return t && t.montant === null;
  });

  // Montants add-on injectes a l'ouverture de la modale (meme patron que le
  // gardiennage ci-dessus).
  // ⚠️ AUCUN FALLBACK A 0. Un montant NULL (prix non defini par le tenant) rend la
  // prestation NON SELECTIONNABLE : chiffrer a 0 produirait une ligne de devis
  // "Videosurveillance — 0" annoncant une prestation contractuellement OFFERTE.
  // On force donc actif=false pour qu'aucune ligne sans prix n'entre au devis.
  const appliquerTarifs = (prev) => {
    const T = (code, periodeDefaut) => {
      const t = tarifsPrest.find(x => x.code === code);
      const defini = !!t && t.montant !== null && t.montant !== undefined;
      return { montant: defini ? Number(t.montant) : 0, periodicite: t ? t.periodicite : periodeDefaut, defini };
    };
    const v = T('video', 'ponctuel'), a = T('alarmes', 'ponctuel'), s = T('tsv', 'mensuel');
    return {
      ...prev,
      gardiennage: { ...prev.gardiennage, agents: totalAgents, tarif },
      video: { ...prev.video, tarif: v.montant, periodicite: v.periodicite, prixDefini: v.defini, actif: v.defini && prev.video.actif },
      alarmes: { ...prev.alarmes, tarif: a.montant, periodicite: a.periodicite, prixDefini: a.defini, actif: a.defini && prev.alarmes.actif },
      tsv: { ...prev.tsv, forfait: s.montant, periodicite: s.periodicite, prixDefini: s.defini, actif: s.defini && prev.tsv.actif },
    };
  };

  // Admin handlers
  // Tarif valide = nombre fini STRICTEMENT positif. `null` arrive quand le champ
  // est vide OU quand MontantInput a refuse la saisie : les deux doivent bloquer.
  // Un 0 se lirait comme une prestation contractuellement OFFERTE (doctrine du 19/08).
  const tarifValide = (v) => {
    const n = Number(v);
    return v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n > 0;
  };

  // Message d'erreur EXACT renvoye par l'API quand il existe (patron deja utilise
  // par handleSaveTarif), jamais un "erreur serveur" generique qui masque la cause.
  const messageErreur = (err, defaut) => err?.response?.data?.error || defaut;

  // ── COMPOSITION ──────────────────────────────────────────────────────────
  // La modale des composants est AUTONOME : elle enregistre elle-meme.
  // `badge` est embarque parce que le backend fait `badge = $6` SANS COALESCE
  // (offreController.js:168) : un PUT partiel l'effacerait en silence.
  const ouvrirCompo = (offre) => {
    setCompoMsg(null);
    setCompo({
      id: offre.id,
      nom: offre.nom,
      badge: offre.badge ?? null,
      description: offre.description || '',
      composants: normaliser(offre.services),
    });
  };

  const majCompo = (code, champ, valeur) => setCompo(c => ({
    ...c,
    composants: c.composants.map(x => x.code === code
      ? { ...x, [champ]: valeur, ...(champ === 'inclus' && !valeur ? { parametre: null } : {}) }
      : x),
  }));

  // Validation AVANT ecriture : un composant inclus sans son engagement est
  // refuse ici, donc jamais enregistre. C'est ce qui rend le « Remplacement
  // sous » vide structurellement impossible.
  // ENREGISTREMENT DIRECT. Le double geste « Valider puis Sauver » etait un
  // defaut d'ergonomie : l'utilisateur croyait avoir enregistre et perdait son
  // travail en quittant la page. Le signe qui ne trompe pas : il avait fallu
  // ECRIRE une explication sous la modale. Quand une interface doit expliquer
  // son propre mecanisme, c'est le mecanisme qu'il faut changer.
  const [enregistrementCompo, setEnregistrementCompo] = useState(false);

  const enregistrerCompo = async () => {
    const v = valider(compo.composants);
    if (!v.ok) { setCompoMsg(v.message); return; }
    setEnregistrementCompo(true);
    try {
      await api.put(`/offres/${compo.id}`, {
        description: compo.description || null,
        services: versJsonb(compo.composants),
        badge: compo.badge,   // renvoye tel quel : sans lui, le backend l'efface
      });
      setCompo(null);
      setCompoMsg(null);
      setAdminMsg({ type: 'success', text: `Composants de "${compo.nom}" enregistres` });
      loadAdminOffres();
      api.get('/offres').then(r => { setOffres(r.data); });
      setTimeout(() => setAdminMsg(null), 3000);
    } catch (err) {
      setCompoMsg(messageErreur(err, "Erreur lors de l'enregistrement — rien n'a ete enregistre"));
    } finally {
      setEnregistrementCompo(false);
    }
  };

  const handleSave = async (row) => {
    // AVANT : `Number(row.tarif_agent_mois)` sans repli. Une saisie invalide donnait
    // NaN, que JSON.stringify serialise en `null` ; le backend faisait alors
    // COALESCE($3, tarif_agent_mois) et CONSERVAIT l'ancien prix — pendant que
    // l'interface affichait "Offre enregistree". Une modification refusee se
    // presentait comme un succes. On bloque desormais AVANT l'appel.
    if (!tarifValide(row.tarif_agent_mois)) {
      setAdminMsg({ type: 'error', text: 'Prix/agent/mois invalide ou absent — modification NON enregistrée' });
      setTimeout(() => setAdminMsg(null), 5000);
      return;
    }
    try {
      await api.put(`/offres/${row.id}`, {
        nom: row.nom, code: row.code,
        tarif_agent_mois: Number(row.tarif_agent_mois),
        badge: row.badge || null, ordre: Number(row.ordre) || 0,
      });
      setEditRow(null);
      setAdminMsg({ type: 'success', text: `Offre "${row.nom}" enregistrée` });
      loadAdminOffres();
      api.get('/offres').then(r => { setOffres(r.data); });
    } catch (err) {
      setAdminMsg({ type: 'error', text: messageErreur(err, 'Erreur lors de la sauvegarde — rien n\'a ete enregistre') });
      setTimeout(() => setAdminMsg(null), 5000);
      return;
    }
    setTimeout(() => setAdminMsg(null), 3000);
  };

  const handleCreate = async () => {
    if (!newRow?.nom || !newRow?.code) {
      setAdminMsg({ type: 'error', text: 'Nom et code requis' });
      setTimeout(() => setAdminMsg(null), 3000);
      return;
    }
    // AVANT : `Number(newRow.tarif_agent_mois) || 0` -> une saisie invalide creait
    // une offre a 0, sans un mot. Un tarif agent a 0 annonce une prestation
    // OFFERTE : on refuse, on ne devine pas.
    if (!tarifValide(newRow.tarif_agent_mois)) {
      setAdminMsg({ type: 'error', text: 'Prix/agent/mois requis et supérieur à 0 — offre NON créée' });
      setTimeout(() => setAdminMsg(null), 5000);
      return;
    }
    try {
      await api.post('/offres', {
        nom: newRow.nom, code: newRow.code,
        tarif_agent_mois: Number(newRow.tarif_agent_mois),
        badge: newRow.badge || null, ordre: Number(newRow.ordre) || 0,
        // Une offre neuve nait SANS composants : ils se saisissent ensuite via
        // « Composants », qui enregistre directement. Rien a composer avant que
        // la ligne existe — il n'y a pas encore d'id sur lequel enregistrer.
        services: versJsonb(normaliser(null)),
      });
      setNewRow(null);
      setAdminMsg({ type: 'success', text: `Offre "${newRow.nom}" créée` });
      loadAdminOffres();
      api.get('/offres').then(r => { setOffres(r.data); });
    } catch (err) {
      setAdminMsg({ type: 'error', text: messageErreur(err, 'Erreur lors de la création — aucune offre créée') });
      setTimeout(() => setAdminMsg(null), 5000);
      return;
    }
    setTimeout(() => setAdminMsg(null), 3000);
  };

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/offres/${row.id}`);
      setAdminMsg({ type: 'success', text: `Offre "${row.nom}" désactivée` });
      loadAdminOffres();
      api.get('/offres').then(r => { setOffres(r.data); });
    } catch { setAdminMsg({ type: 'error', text: 'Erreur lors de la désactivation' }); }
    setTimeout(() => setAdminMsg(null), 3000);
  };

  const handleReactivate = async (row) => {
    try {
      await api.put(`/offres/${row.id}`, { actif: true });
      setAdminMsg({ type: 'success', text: `Offre "${row.nom}" réactivée` });
      loadAdminOffres();
      api.get('/offres').then(r => { setOffres(r.data); });
    } catch { setAdminMsg({ type: 'error', text: 'Erreur lors de la réactivation' }); }
    setTimeout(() => setAdminMsg(null), 3000);
  };

  // Sauvegarde d'un tarif de prestation. Le code n'est PAS envoye (cle technique non modifiable).
  // Affiche le message d'erreur EXACT renvoye par l'API (400 montant/mode/periodicite, 409 doublon),
  // pas un "erreur serveur" generique.
  const handleSaveTarif = async (row) => {
    try {
      // Champ vide => null EXPLICITE ("prix a definir"), surtout PAS Number('') === 0
      // qui annoncerait une prestation offerte. L'API distingue champ absent
      // (ne pas toucher) de null explicite (retirer le prix).
      const montantSaisi = String(row.montant ?? '').trim();
      await api.put(`/tarifs-prestations/${row.id}`, {
        libelle: row.libelle,
        montant: montantSaisi === '' ? null : Number(montantSaisi),
        mode: row.mode,
        periodicite: row.periodicite,
      });
      setEditTarif(null);
      setTarifMsg({ type: 'success', text: `Tarif "${row.libelle}" enregistre` });
      loadTarifsPrest();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Erreur lors de la sauvegarde';
      setTarifMsg({ type: 'error', text: msg });
    }
    setTimeout(() => setTarifMsg(null), 4000);
  };

  // Suffixe de libelle aligne sur la periodicite reelle de la ligne.
  const SUFFIXE_PERIODE = { mensuel: 'tarif mensuel', ponctuel: 'à la commande' };
  // Sections de la modale -> lignes[] du devis. Chaque ligne porte SA periodicite (ponctuel|mensuel).
  const sectionsToLignes = (ds) => {
    const L = [];
    const suf = (p) => SUFFIXE_PERIODE[p] || 'tarif mensuel';
    if (ds.gardiennage.actif)
      L.push({ designation: `Gardiennage — ${ds.gardiennage.agents} agent${ds.gardiennage.agents > 1 ? 's' : ''} (${suf(ds.gardiennage.periodicite)})`,
               quantite: ds.gardiennage.agents, prix_unitaire_ht: ds.gardiennage.tarif, periodicite: ds.gardiennage.periodicite });
    if (ds.video.actif)
      L.push({ designation: `Vidéosurveillance — ${ds.video.cameras} caméra${ds.video.cameras > 1 ? 's' : ''} (${suf(ds.video.periodicite)})`,
               quantite: ds.video.cameras, prix_unitaire_ht: ds.video.tarif, periodicite: ds.video.periodicite });
    if (ds.alarmes.actif)
      L.push({ designation: `Alarmes & Détection — ${ds.alarmes.zones} zone${ds.alarmes.zones > 1 ? 's' : ''} (${suf(ds.alarmes.periodicite)})`,
               quantite: ds.alarmes.zones, prix_unitaire_ht: ds.alarmes.tarif, periodicite: ds.alarmes.periodicite });
    if (ds.tsv.actif)
      L.push({ designation: `Télésurveillance — forfait (${suf(ds.tsv.periodicite)})`,
               quantite: 1, prix_unitaire_ht: ds.tsv.forfait, periodicite: ds.tsv.periodicite });
    return L;
  };

  // Cree un VRAI devis persiste via POST /devis (meme controleur que QuotesPage : creerDevis),
  // puis enchaine sur le PDF (GET /devis/:id/pdf). Fallback message si le PDF echoue.
  const handleGenererDevis = async () => {
    if (!clientValueValid(clientValue)) { alert('Sélectionnez un client (ou saisissez une raison sociale).'); return; }
    if (!devisObjet.trim()) { alert("L'objet du devis est requis."); return; }
    const lignes = sectionsToLignes(devisSections);
    if (lignes.length === 0) { alert('Au moins une prestation doit être active.'); return; }

    setSavingDevis(true);
    try {
      const payload = {
        ...clientPayload(clientValue),     // client_id OU nouveau_client
        objet: devisObjet.trim(),
        date_validite: devisDateValidite || null,
        lignes,
      };
      const res = await api.post('/devis', payload);   // 201 -> devis cree (transaction backend)
      const devisId = res.data?.id;
      setShowDevisModal(false);

      if (devisId) {
        try {
          const pdf = await api.get(`/devis/${devisId}/pdf`, { responseType: 'blob' });
          const url = URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' }));
          window.open(url, '_blank');
        } catch {
          alert('Devis cree. Le PDF n\'a pas pu etre genere — il reste consultable depuis le module Devis.');
        }
      }

      // reset : client/objet/date vides ; sections CONSERVEES (re-choisir un client pour re-soumettre)
      setClientValue(emptyClientValue());
      setDevisObjet('');
      setDevisDateValidite('');
    } catch (e) {
      // couvre le 403 garde-fou comptable (message backend) et les erreurs de validation
      alert(e?.response?.data?.error || 'Erreur lors de la création du devis.');
    } finally {
      setSavingDevis(false);
    }
  };

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Chargement...</p>;

  return (
    <div>
      {/* ═══ PRICING CARDS ═══ */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 30, alignItems: 'stretch', justifyContent: 'center' }}>
        {[...offres]
          .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)
                       || Number(a.tarif_agent_mois || 0) - Number(b.tarif_agent_mois || 0))
          .map((o, rang, gamme) => {
          const style = styleParRang(rang, gamme.length);
          const isSelected = selected === o.id;

          // Libelles COMPOSES avec leur parametre (« Remplacement sous 2h »).
          // `lignesAffichage` lit indifferemment l'ancien format plat (seed Fox,
          // toujours en prod) et le nouveau format structure — voir composants-offre.js.
          // Les composants INCLUS d'abord : une carte doit vendre ce qu'elle offre
          // avant de montrer ce qu'elle n'offre pas.
          const lignes = lignesAffichage(o.services);
          const lignesTriees = [...lignes.filter(l => l.inclus), ...lignes.filter(l => !l.inclus)];

          return (
            <div key={o.id} style={{
              flex: 1, maxWidth: 300, borderRadius: 16, padding: 16, position: 'relative', overflow: 'hidden',
              background: style.bg, border: `2px solid ${isSelected ? '#8B1A1A' : style.border}`,
              boxShadow: isSelected ? '0 0 0 3px rgba(139,26,26,0.2)' : '0 2px 12px rgba(0,0,0,0.06)',
              transition: 'all 200ms', display: 'flex', flexDirection: 'column',
            }}>
              {/* ⚠️ AVANT : la carte affichait `style.badge`, une valeur EN DUR, et
                  n'a JAMAIS lu la colonne `offres.badge`. Celle-ci est pourtant
                  saisissable dans l'admin, persistee et affichee dans le tableau :
                  un champ stocke mais NON CABLE, qui donne l'illusion d'un reglage
                  actif (meme motif que le fuseau horaire, cf. CLAUDE.md).
                  Fox affichait « POPULAIRE » sans donnee ; le « RECOMMANDE » saisi
                  sur l'ESSENTIELLE de Gladiator, lui, ne s'affichait pas. */}
              {o.badge && (
                <div style={{
                  position: 'absolute', top: 0, right: 0, padding: '4px 16px', borderRadius: '0 14px 0 14px',
                  // La couleur suit la CARTE, pas le texte du badge : celui-ci est
                  // libre, on ne peut pas s'appuyer sur sa valeur.
                  background: style.dark ? '#C8A84E' : '#8B1A1A',
                  color: '#FFF', fontSize: 9, fontWeight: 800, letterSpacing: 2,
                }}>{o.badge}</div>
              )}

              <div style={{ fontSize: 14, fontWeight: 800, color: style.title, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
                {o.nom}
              </div>
              <div style={{ fontSize: 10, color: style.dark ? '#888' : '#999', marginBottom: 16, fontWeight: 400 }}>{o.description}</div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: style.dark ? '#C8A84E' : '#8B1A1A' }}>
                  {Number(o.tarif_agent_mois).toLocaleString()}
                </span>
                <span style={{ fontSize: 10, color: style.dark ? '#888' : '#999', fontWeight: 400 }}> {DEVISE} / agent / mois</span>
              </div>

              <div style={{ flex: 1 }}>
                {lignesTriees.map(l => (
                  <ServiceLine key={l.code} label={l.libelle} included={l.inclus} dark={style.dark} />
                ))}
              </div>

              <button
                onClick={() => { setSelected(o.id); setSimOffre(o.id); }}
                style={{
                  width: '100%', marginTop: 'auto', padding: '10px 0', borderRadius: 10, border: 'none',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', letterSpacing: 1,
                  background: style.dark ? '#C8A84E' : '#8B1A1A', color: '#FFF',
                }}>
                Sélectionner
              </button>
            </div>
          );
        })}
      </div>

      {/* ═══ SIMULATEUR ═══ */}
      <div className="content-card" style={{ padding: 24 }}>
        <div className="content-card-title" style={{ marginBottom: 16 }}>Simulateur de devis</div>

        <div className="gss-form-grid">
          <div className="gss-field">
            <label>Offre sélectionnée</label>
            <select className="gss-input" value={simOffre} onChange={e => setSimOffre(e.target.value)}>
              {offres.map(o => <option key={o.id} value={o.id}>{o.nom} — {Number(o.tarif_agent_mois).toLocaleString()} {DEVISE}</option>)}
            </select>
          </div>
          <div className="gss-field">
            <label>Nombre de sites</label>
            <input className="gss-input" type="number" min="1" value={nbSites} onChange={e => setNbSites(parseInt(e.target.value) || 1)} />
          </div>
          <div className="gss-field">
            <label>Agents jour / site</label>
            <input className="gss-input" type="number" min="0" value={agentsJour} onChange={e => setAgentsJour(parseInt(e.target.value) || 0)} />
          </div>
          <div className="gss-field">
            <label>Agents nuit / site</label>
            <input className="gss-input" type="number" min="0" value={agentsNuit} onChange={e => setAgentsNuit(parseInt(e.target.value) || 0)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginTop: 20 }}>
          <div style={{ padding: 16, background: '#F8F9FA', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 400 }}>Total agents</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#1B3A5C' }}>{totalAgents}</div>
          </div>
          <div style={{ padding: 16, background: '#F0FDF4', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 400 }}>Mensuel</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#22C55E' }}>{mensuel.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: '#999', fontWeight: 400 }}>{DEVISE}</div>
          </div>
          <div style={{ padding: 16, background: '#EFF6FF', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 400 }}>Trimestriel (-5%)</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#1565C0' }}>{trimestriel.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: '#999', fontWeight: 400 }}>{DEVISE}</div>
          </div>
          <div style={{ padding: 16, background: '#1A1A2E', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#C8A84E', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 400 }}>Annuel (-15%)</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#FFFFFF' }}>{annuel.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>{DEVISE}</div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowEmailModal(true)}
            style={{
              height: 36, padding: '0 20px', borderRadius: 8, border: '1px solid #8B1A1A',
              background: '#FFF', color: '#8B1A1A', fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}>
            Envoyer la proposition au client
          </button>
          <button
            disabled={loadingTarifs}
            onClick={() => {
              // Injecte les montants du tenant (gardiennage + les 3 add-on) avant
              // d'ouvrir : la modale n'affiche jamais de prix devine.
              setDevisSections(appliquerTarifs);
              const briques = ['Gardiennage'];
              if (devisSections.video.actif) briques.push('Vidéo');
              if (devisSections.alarmes.actif) briques.push('Alarmes');
              if (devisSections.tsv.actif) briques.push('Télésurveillance');
              setDevisObjet(`Offre ${simOffreObj?.nom || ''} — ${briques.join(' + ')}`);
              setShowDevisModal(true);
            }}
            style={{
              height: 36, padding: '0 20px', borderRadius: 8, border: 'none',
              background: loadingTarifs ? '#BBB' : '#8B1A1A', color: '#FFF', fontWeight: 700, fontSize: 13,
              cursor: loadingTarifs ? 'wait' : 'pointer',
            }}>
            {loadingTarifs ? 'Chargement des tarifs...' : 'Générer devis PDF'}
          </button>
        </div>
      </div>

      {/* ═══ MODAL DEVIS — COMPOSITION 4 SECTIONS ═══ */}
      {showDevisModal && (() => {
        const ds = devisSections;
        const setDS = (key, val) => setDevisSections(prev => ({ ...prev, [key]: { ...prev[key], ...val } }));
        const totalGard = ds.gardiennage.actif ? ds.gardiennage.agents * ds.gardiennage.tarif : 0;
        const totalVideo = ds.video.actif ? ds.video.cameras * ds.video.tarif : 0;
        const totalAlarmes = ds.alarmes.actif ? ds.alarmes.zones * ds.alarmes.tarif : 0;
        const totalTSV = ds.tsv.actif ? ds.tsv.forfait : 0;
        // Deux totaux separes par periodicite — JAMAIS additionnes (miroir des 2 blocs du PDF).
        const parPeriode = (p) =>
          (ds.gardiennage.actif && ds.gardiennage.periodicite === p ? totalGard : 0) +
          (ds.video.actif && ds.video.periodicite === p ? totalVideo : 0) +
          (ds.alarmes.actif && ds.alarmes.periodicite === p ? totalAlarmes : 0) +
          (ds.tsv.actif && ds.tsv.periodicite === p ? totalTSV : 0);
        const totalPonctuel = parPeriode('ponctuel');
        const totalMensuel = parPeriode('mensuel');

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setShowDevisModal(false)}>
            <div style={{ background: '#FFF', borderRadius: 16, padding: 28, width: 560, maxHeight: '85vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>Composer le devis client</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Offre {simOffreObj?.nom} — {tarif.toLocaleString()} {DEVISE}/agent/mois</div>

              {/* Prestation sans prix -> NON SELECTIONNABLE (jamais chiffree a 0).
                  On distingue "absente du catalogue" de "presente mais a tarifer". */}
              {(tarifsManquants.length + prixNonDefinis.length) > 0 && (
                <div style={{ background: '#FFF4E5', border: '1px solid #E8A33D', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12, color: '#7A4B00' }}>
                  Prix non defini pour : {[...prixNonDefinis, ...tarifsManquants].map(c => LIBELLES_PRESTA[c]).join(', ')}.
                  Ces prestations sont <strong>désactivées</strong> et ne peuvent pas entrer dans le devis tant qu'un prix
                  n'est pas renseigne. {isAdmin ? 'Renseignez-les dans la zone admin de cette page.' : 'Demandez à un administrateur de les renseigner.'}
                </div>
              )}

              {/* Client + Objet + Date validité */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <ClientPicker clients={clients} value={clientValue} onChange={setClientValue} roles={user?.roles || []} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' }}>Objet du devis *</label>
                  <input className="gss-input" value={devisObjet} onChange={e => setDevisObjet(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' }}>Date de validité</label>
                  <input className="gss-input" type="date" value={devisDateValidite} onChange={e => setDevisDateValidite(e.target.value)} style={{ width: '100%' }} />
                </div>
              </div>

              {/* A — Gardiennage */}
              <DevisSection
                title="A — Gardiennage" checked={ds.gardiennage.actif} locked
                onToggle={() => {}}
                periodicite={ds.gardiennage.periodicite} onPeriodicite={v => setDS('gardiennage', { periodicite: v })}
                total={totalGard}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <label style={MS.label}>Agents</label>
                  <input type="number" min="1" value={ds.gardiennage.agents}
                    onChange={e => setDS('gardiennage', { agents: parseInt(e.target.value) || 1 })}
                    style={MS.numInput} />
                  <span style={{ fontSize: 12, color: '#888' }}>x {ds.gardiennage.tarif.toLocaleString()} {DEVISE}</span>
                </div>
              </DevisSection>

              {/* B — Vidéosurveillance */}
              <DevisSection
                title="B — Vidéosurveillance" checked={ds.video.actif}
                locked={!ds.video.prixDefini} note={ds.video.prixDefini ? null : 'prix à définir'}
                onToggle={() => ds.video.prixDefini && setDS('video', { actif: !ds.video.actif })}
                periodicite={ds.video.periodicite} onPeriodicite={v => setDS('video', { periodicite: v })}
                total={totalVideo}>
                {ds.video.actif && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={MS.label}>Cameras</label>
                    <input type="number" min="1" value={ds.video.cameras}
                      onChange={e => setDS('video', { cameras: parseInt(e.target.value) || 1 })}
                      style={MS.numInput} />
                    <span style={{ fontSize: 12, color: '#888' }}>x {ds.video.tarif.toLocaleString()} {DEVISE}</span>
                  </div>
                )}
              </DevisSection>

              {/* C — Alarmes & Détection */}
              <DevisSection
                title="C — Alarmes & Détection" checked={ds.alarmes.actif}
                locked={!ds.alarmes.prixDefini} note={ds.alarmes.prixDefini ? null : 'prix à définir'}
                onToggle={() => ds.alarmes.prixDefini && setDS('alarmes', { actif: !ds.alarmes.actif })}
                periodicite={ds.alarmes.periodicite} onPeriodicite={v => setDS('alarmes', { periodicite: v })}
                total={totalAlarmes}>
                {ds.alarmes.actif && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={MS.label}>Zones</label>
                    <input type="number" min="1" value={ds.alarmes.zones}
                      onChange={e => setDS('alarmes', { zones: parseInt(e.target.value) || 1 })}
                      style={MS.numInput} />
                    <span style={{ fontSize: 12, color: '#888' }}>x {ds.alarmes.tarif.toLocaleString()} {DEVISE}</span>
                  </div>
                )}
              </DevisSection>

              {/* D — Télésurveillance */}
              <DevisSection
                title="D — Télésurveillance" checked={ds.tsv.actif}
                locked={!ds.tsv.prixDefini} note={ds.tsv.prixDefini ? null : 'prix à définir'}
                onToggle={() => ds.tsv.prixDefini && setDS('tsv', { actif: !ds.tsv.actif })}
                periodicite={ds.tsv.periodicite} onPeriodicite={v => setDS('tsv', { periodicite: v })}
                total={totalTSV}>
                {ds.tsv.actif && (
                  <div style={{ fontSize: 12, color: '#888' }}>Forfait {ds.tsv.periodicite === 'mensuel' ? 'mensuel' : 'à la commande'} : {ds.tsv.forfait.toLocaleString()} {DEVISE}</div>
                )}
              </DevisSection>

              {/* Deux totaux — JAMAIS additionnes (a la commande vs mensuel) */}
              <div style={{ borderTop: '2px solid #1A1A1A', marginTop: 8, paddingTop: 12 }}>
                {totalPonctuel > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A' }}>Total à la commande</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: '#8B1A1A' }}>{totalPonctuel.toLocaleString()} {DEVISE}</span>
                  </div>
                )}
                {totalMensuel > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A' }}>Total mensuel</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: '#8B1A1A' }}>{totalMensuel.toLocaleString()} {DEVISE}<span style={{ fontSize: 12, fontWeight: 600 }}> /mois</span></span>
                  </div>
                )}
                {totalPonctuel === 0 && totalMensuel === 0 && (
                  <div style={{ fontSize: 12, color: '#999', textAlign: 'center', padding: '4px 0' }}>Aucune prestation active</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setShowDevisModal(false)}
                  style={{ height: 36, padding: '0 20px', borderRadius: 8, border: '1px solid #DDD', background: '#FFF', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Annuler
                </button>
                <button
                  onClick={handleGenererDevis} disabled={savingDevis}
                  style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: savingDevis ? '#CCC' : '#8B1A1A', color: '#FFF', fontWeight: 700, fontSize: 13, cursor: savingDevis ? 'default' : 'pointer' }}>
                  {savingDevis ? 'Création...' : 'Générer le devis'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ MODAL COMPOSITION D'OFFRE ═══ */}
      {compo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setCompo(null); setCompoMsg(null); }}>
          <div style={{ background: '#FFF', borderRadius: 16, padding: 28, width: 620, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>Composants de l'offre</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              Un composant inclus doit porter son engagement — c'est lui qui justifie l'écart de prix.
            </div>

            <div className="gss-field" style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' }}>Description</label>
              <input className="gss-input" value={compo.description}
                onChange={e => setCompo(c => ({ ...c, description: e.target.value }))}
                placeholder="Une ligne, affichée sous le nom de l'offre" style={{ width: '100%' }} />
            </div>

            {compoMsg && (
              <div style={{ padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 500,
                background: 'var(--gss-danger)', color: '#FFF' }}>{compoMsg}</div>
            )}

            <div style={{ border: '1px solid var(--gss-border)', borderRadius: 8, overflow: 'hidden' }}>
              {compo.composants.map(c => {
                const def = definition(c.code);
                const manque = c.inclus && def.parametre && !estValeurValide(c.parametre);
                return (
                  <div key={c.code} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderBottom: '1px solid var(--gss-border)',
                    background: manque ? '#FFF4E5' : (c.inclus ? '#F0FDF4' : '#FFF'),
                  }}>
                    <input type="checkbox" checked={c.inclus}
                      onChange={e => majCompo(c.code, 'inclus', e.target.checked)} />
                    {/* MEME fonction que la carte (`libelleAffiche`) : le texte de la
                        modale et celui que lira le client ne peuvent PAS diverger.
                        Auparavant la modale disait « Continuité de service » et la
                        carte « Agent absent remplacé sous 1h » — on croyait sa saisie
                        perdue. L'utilisateur n'a pas a traduire entre les deux. */}
                    <span style={{ flex: 1, fontSize: 13, color: c.inclus ? '#1A1A1A' : '#888' }}>
                      {libelleAffiche(c.code, c.parametre)}
                      {def.parametre && c.inclus && estValeurValide(c.parametre) && (
                        <span style={{ fontSize: 10, color: '#888', marginLeft: 8, fontStyle: 'italic' }}>
                          tel que le client le lira
                        </span>
                      )}
                    </span>

                    {/* SAISIE LIBRE : nombre + unite. AUCUNE liste fermee — un tenant
                        qui ne tient que 6h doit pouvoir s'engager sur 6h, pas cocher
                        4h faute de mieux et se mettre en defaut au suivi SLA.
                        Les suggestions ci-dessous sont de l'ERGONOMIE, pas une contrainte. */}
                    {def.parametre && c.inclus && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: '#888' }}>{def.parametre.libelle}</span>
                        <input className="gss-input" type="number" min="0" step="any"
                          placeholder="—" style={{ width: 74, fontSize: 12 }}
                          value={c.parametre?.nombre ?? ''}
                          onChange={e => majCompo(c.code, 'parametre', e.target.value === '' ? null : {
                            nombre: Number(e.target.value),
                            unite: c.parametre?.unite ?? def.parametre.uniteDefaut,
                          })} />
                        <select className="gss-input" style={{ width: 130, fontSize: 12 }}
                          value={c.parametre?.unite ?? def.parametre.uniteDefaut}
                          onChange={e => majCompo(c.code, 'parametre', {
                            nombre: c.parametre?.nombre ?? '',
                            unite: e.target.value,
                          })}>
                          {unitesAutorisees(def).map(u => (
                            <option key={u} value={u}>{LIBELLE_UNITE[u]}</option>
                          ))}
                        </select>
                        {/* Plage habituelle : on SIGNALE, on ne refuse pas. Un site
                            isole peut legitimement annoncer 8h de continuite. */}
                        {valeurHorsPlage(c.code, c.parametre) && (
                          <span title={valeurHorsPlage(c.code, c.parametre)}
                            style={{ fontSize: 10, color: '#B26A00', background: '#FFF4E5',
                                     border: '1px solid #E8A33D', borderRadius: 5, padding: '1px 6px' }}>
                            inhabituel
                          </span>
                        )}
                        {/* Suggestions : un clic remplit, rien n'oblige a les utiliser. */}
                        {def.parametre.suggestions.map((s, i) => (
                          <button key={i} type="button" onClick={() => majCompo(c.code, 'parametre', s)}
                            title="Suggestion — vous pouvez saisir toute autre valeur"
                            style={{
                              border: '1px solid var(--gss-border)', background: '#FFF', borderRadius: 999,
                              padding: '1px 8px', fontSize: 10, color: '#666', cursor: 'pointer',
                            }}>{rendreValeur(s)}</button>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { setCompo(null); setCompoMsg(null); }}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: '1px solid #DDD', background: '#FFF', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={enregistrerCompo} disabled={enregistrementCompo}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none',
                         background: enregistrementCompo ? '#CCC' : '#8B1A1A', color: '#FFF',
                         fontWeight: 700, fontSize: 13, cursor: enregistrementCompo ? 'default' : 'pointer' }}>
                {enregistrementCompo ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ═══ MODAL EMAIL ═══ */}
      {showEmailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowEmailModal(false)}>
          <div style={{ background: '#FFF', borderRadius: 16, padding: 28, width: 440 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', marginBottom: 16 }}>Envoyer la proposition</div>

            <div style={{ padding: 16, background: '#F8F9FA', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#555' }}>
                Offre <strong>{simOffreObj?.nom}</strong> — {totalAgents} agents — {mensuel.toLocaleString()} {DEVISE}/mois
              </div>
            </div>

            <div className="gss-field" style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' }}>
                Email du client
              </label>
              <input
                className="gss-input"
                type="email"
                placeholder="client@entreprise.tg"
                value={clientEmail}
                onChange={e => setClientEmail(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowEmailModal(false)}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: '1px solid #DDD', background: '#FFF', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Annuler
              </button>
              <button
                onClick={() => { setShowEmailModal(false); setClientEmail(''); alert('Proposition envoyee a ' + clientEmail); }}
                disabled={!clientEmail}
                style={{
                  height: 36, padding: '0 20px', borderRadius: 8, border: 'none',
                  background: clientEmail ? '#8B1A1A' : '#CCC', color: '#FFF', fontWeight: 700, fontSize: 13,
                  cursor: clientEmail ? 'pointer' : 'default',
                }}>
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADMIN — GESTION DES OFFRES ═══ */}
      {isAdmin && (
        <div className="content-card" style={{ padding: 24, marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="content-card-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
              Gestion des offres
            </div>
            <button
              /* tarif a `null` et NON a 0 : le champ doit naitre VIDE. Un 0 pre-rempli
                 se laisse valider par inadvertance et cree une offre "offerte". */
              onClick={() => setNewRow({ nom: '', code: '', tarif_agent_mois: null, badge: '', ordre: 0 })}
              disabled={!!newRow}
              style={{
                height: 32, padding: '0 16px', borderRadius: 8, border: 'none',
                background: newRow ? 'var(--gss-border-strong)' : 'var(--gss-red)',
                color: '#FFF', fontWeight: 600, fontSize: 12, cursor: newRow ? 'default' : 'pointer',
              }}>
              + Ajouter une offre
            </button>
          </div>

          {adminMsg && (
            <div style={{
              padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 500,
              background: adminMsg.type === 'success' ? 'var(--gss-success)' : 'var(--gss-danger)',
              color: '#FFF',
            }}>{adminMsg.text}</div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gss-border-strong)' }}>
                  {['Ordre', 'Nom', 'Code', 'Prix/agent/mois', 'Badge', 'Actif', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--gss-text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* New row */}
                {newRow && (
                  <tr style={{ borderBottom: '1px solid var(--gss-border)', background: 'var(--gss-red-glow)' }}>
                    <td style={TD}><input className="gss-input" type="number" value={newRow.ordre} onChange={e => setNewRow({ ...newRow, ordre: e.target.value })} style={{ ...TINPUT, width: 60 }} /></td>
                    <td style={TD}><input className="gss-input" value={newRow.nom} onChange={e => setNewRow({ ...newRow, nom: e.target.value })} style={TINPUT} placeholder="Nom *" /></td>
                    <td style={TD}><input className="gss-input" value={newRow.code} onChange={e => setNewRow({ ...newRow, code: e.target.value.toUpperCase() })} style={{ ...TINPUT, width: 70 }} placeholder="CODE *" maxLength={5} /></td>
                    {/* type="number" RETIRE : c'est lui qui amputait « 125 000 » en 125
                        avant meme que JavaScript ne voie la saisie (bug prod 22/08/2026). */}
                    <td style={TD}><MontantInput value={newRow.tarif_agent_mois} onChange={v => setNewRow({ ...newRow, tarif_agent_mois: v })} afficherDevise={false} placeholder="Prix *" style={{ ...TINPUT, width: 120 }} /></td>
                    <td style={TD}><input className="gss-input" value={newRow.badge} onChange={e => setNewRow({ ...newRow, badge: e.target.value })} style={{ ...TINPUT, width: 120 }} placeholder="ex: POPULAIRE" /></td>
                    <td style={TD}><span style={{ color: 'var(--gss-success)', fontWeight: 600 }}>Oui</span></td>
                    <td style={TD}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={handleCreate} style={BTN_OK}>Créer</button>
                        <button onClick={() => setNewRow(null)} style={BTN_CANCEL}>Annuler</button>
                      </div>
                    </td>
                  </tr>
                )}

                {adminOffres.map(o => {
                  const isEditing = editRow?.id === o.id;
                  const row = isEditing ? editRow : o;
                  return (
                    <tr key={o.id} style={{ borderBottom: '1px solid var(--gss-border)', opacity: o.actif ? 1 : 0.5 }}>
                      <td style={TD}>
                        {isEditing
                          ? <input className="gss-input" type="number" value={row.ordre} onChange={e => setEditRow({ ...editRow, ordre: e.target.value })} style={{ ...TINPUT, width: 60 }} />
                          : row.ordre}
                      </td>
                      <td style={{ ...TD, fontWeight: 600, color: 'var(--gss-text)' }}>
                        {isEditing
                          ? <input className="gss-input" value={row.nom} onChange={e => setEditRow({ ...editRow, nom: e.target.value })} style={TINPUT} />
                          : row.nom}
                      </td>
                      <td style={TD}>
                        {isEditing
                          ? <input className="gss-input" value={row.code} onChange={e => setEditRow({ ...editRow, code: e.target.value.toUpperCase() })} style={{ ...TINPUT, width: 70 }} maxLength={5} />
                          : <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--gss-border)', fontSize: 11, fontWeight: 700 }}>{row.code}</span>}
                      </td>
                      <td style={TD}>
                        {isEditing
                          ? <MontantInput value={row.tarif_agent_mois} onChange={v => setEditRow({ ...editRow, tarif_agent_mois: v })} afficherDevise={false} style={{ ...TINPUT, width: 120 }} />
                          : <span style={{ fontWeight: 600 }}>{formatMontant(row.tarif_agent_mois, DEVISE)} {DEVISE}</span>}
                      </td>
                      <td style={TD}>
                        {isEditing
                          ? <input className="gss-input" value={row.badge || ''} onChange={e => setEditRow({ ...editRow, badge: e.target.value })} style={{ ...TINPUT, width: 120 }} />
                          : row.badge ? <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--gss-gold-bg)', color: 'var(--gss-gold)', fontSize: 10, fontWeight: 700 }}>{row.badge}</span> : <span style={{ color: 'var(--gss-text-light)' }}>—</span>}
                      </td>
                      <td style={TD}>
                        <span style={{ color: o.actif ? 'var(--gss-success)' : 'var(--gss-danger)', fontWeight: 600 }}>
                          {o.actif ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td style={TD}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {isEditing ? (
                            <>
                              <button onClick={() => handleSave(editRow)} style={BTN_OK}>Sauver</button>
                              <button onClick={() => setEditRow(null)} style={BTN_CANCEL}>Annuler</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditRow({ ...o })} style={BTN_EDIT}>Modifier</button>
                              <button onClick={() => ouvrirCompo(o)} style={BTN_EDIT}>Composants</button>
                              {o.actif
                                ? <button onClick={() => handleDeactivate(o)} style={BTN_DANGER}>Désactiver</button>
                                : <button onClick={() => handleReactivate(o)} style={BTN_OK}>Réactiver</button>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ ADMIN — TARIFS DES PRESTATIONS ═══ */}
      {isAdmin && (
        <div className="content-card" style={{ padding: 24, marginTop: 24 }}>
          <div className="content-card-title" style={{ marginBottom: 16 }}>
            Tarifs des prestations
          </div>

          {tarifMsg && (
            <div style={{
              padding: '8px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 500,
              background: tarifMsg.type === 'success' ? 'var(--gss-success)' : 'var(--gss-danger)',
              color: '#FFF',
            }}>{tarifMsg.text}</div>
          )}

          {loadingTarifs ? (
            <div style={{ padding: 16, color: 'var(--gss-text-light)', fontSize: 13 }}>Chargement…</div>
          ) : tarifsPrest.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--gss-text-light)', fontSize: 13 }}>Aucun tarif de prestation configuré.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gss-border-strong)' }}>
                    {['Prestation', 'Code', 'Montant', 'Mode', 'Périodicité', 'Actif', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--gss-text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tarifsPrest.map(t => {
                    const isEditing = editTarif?.id === t.id;
                    const row = isEditing ? editTarif : t;
                    return (
                      <tr key={t.id} style={{ borderBottom: '1px solid var(--gss-border)', opacity: t.actif ? 1 : 0.5 }}>
                        <td style={{ ...TD, fontWeight: 600, color: 'var(--gss-text)' }}>
                          {isEditing
                            ? <input className="gss-input" value={row.libelle} onChange={e => setEditTarif({ ...editTarif, libelle: e.target.value })} style={TINPUT} />
                            : row.libelle}
                        </td>
                        <td style={TD}>
                          {/* Code = cle technique liee au catalogue fige : NON modifiable */}
                          <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--gss-border)', fontSize: 11, fontWeight: 700 }}>{t.code}</span>
                        </td>
                        <td style={TD}>
                          {/* montant NULL = prix pas encore fixe par le tenant. Champ VIDE
                              (pas 0) en edition, mention explicite en lecture : un "0 F"
                              se lirait comme une prestation offerte. */}
                          {isEditing
                            ? <input className="gss-input" type="number" min="0" placeholder="à définir"
                                value={row.montant ?? ''}
                                onChange={e => setEditTarif({ ...editTarif, montant: e.target.value })}
                                style={{ ...TINPUT, width: 120 }} />
                            : row.montant === null
                              ? <span style={{ fontWeight: 600, color: '#B26A00', background: '#FFF4E5', border: '1px solid #E8A33D', borderRadius: 5, padding: '2px 8px', fontSize: 11 }}>prix à définir</span>
                              : <span style={{ fontWeight: 600 }}>{Number(row.montant).toLocaleString()} {DEVISE}</span>}
                        </td>
                        <td style={TD}>
                          {isEditing
                            ? <select className="gss-input" value={row.mode} onChange={e => setEditTarif({ ...editTarif, mode: e.target.value })} style={{ ...TINPUT, width: 110 }}>
                                <option value="unitaire">unitaire</option>
                                <option value="forfait">forfait</option>
                              </select>
                            : row.mode}
                        </td>
                        <td style={TD}>
                          {isEditing
                            ? <select className="gss-input" value={row.periodicite} onChange={e => setEditTarif({ ...editTarif, periodicite: e.target.value })} style={{ ...TINPUT, width: 110 }}>
                                <option value="ponctuel">ponctuel</option>
                                <option value="mensuel">mensuel</option>
                              </select>
                            : row.periodicite}
                        </td>
                        <td style={TD}>
                          <span style={{ color: t.actif ? 'var(--gss-success)' : 'var(--gss-danger)', fontWeight: 600 }}>
                            {t.actif ? 'Oui' : 'Non'}
                          </span>
                        </td>
                        <td style={TD}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {isEditing ? (
                              <>
                                <button onClick={() => handleSaveTarif(editTarif)} style={BTN_OK}>Sauver</button>
                                <button onClick={() => setEditTarif(null)} style={BTN_CANCEL}>Annuler</button>
                              </>
                            ) : (
                              <button onClick={() => setEditTarif({ ...t })} style={BTN_EDIT}>Modifier</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DevisSection({ title, checked, locked, note, onToggle, total, periodicite, onPeriodicite, children }) {
  return (
    <div style={{ padding: 12, background: checked ? '#F8F9FA' : '#FAFAFA', borderRadius: 10, marginBottom: 8, border: checked ? '1px solid #E0E0E0' : '1px solid #F0F0F0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: checked ? 8 : 0 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: locked ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={checked} onChange={onToggle} disabled={locked}
            style={{ accentColor: '#8B1A1A' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: checked ? '#1A1A1A' : '#999' }}>{title}</span>
          {/* Motif du verrouillage (ex. prix non defini) : dit POURQUOI la case est grisee. */}
          {note && <span style={{ fontSize: 11, fontWeight: 600, color: '#B26A00', background: '#FFF4E5', border: '1px solid #E8A33D', borderRadius: 5, padding: '1px 6px' }}>{note}</span>}
        </label>
        {checked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onPeriodicite && (
              <select value={periodicite} onChange={e => onPeriodicite(e.target.value)}
                style={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, border: '1px solid #DDD' }}
                title="Périodicité de cette prestation">
                <option value="ponctuel">Ponctuel</option>
                <option value="mensuel">Mensuel</option>
              </select>
            )}
            <span style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A' }}>{total.toLocaleString()} F</span>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

const MS = {
  label: { fontSize: 12, color: '#555', minWidth: 60 },
  numInput: { width: 70, padding: '6px 10px', borderRadius: 8, border: '1px solid #DDD', fontSize: 13, textAlign: 'center' },
};

// Admin table styles
const TD = { padding: '10px 10px', verticalAlign: 'middle', color: 'var(--gss-text-secondary)' };
const TINPUT = { height: 32, fontSize: 13, padding: '0 8px', borderRadius: 6 };
const BTN_BASE = { height: 28, padding: '0 12px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const BTN_OK = { ...BTN_BASE, background: 'var(--gss-success)', color: '#FFF' };
const BTN_CANCEL = { ...BTN_BASE, background: 'var(--gss-border-strong)', color: 'var(--gss-text-secondary)' };
const BTN_EDIT = { ...BTN_BASE, background: 'var(--gss-info)', color: '#FFF' };
const BTN_DANGER = { ...BTN_BASE, background: 'var(--gss-danger)', color: '#FFF' };
